<template>
    <AlertDialog />
</template>

<script setup lang="ts">
import AlertDialog from './components/dialogs/AlertDialog.vue'
import { onMounted } from 'vue'
import { openAlertDialog, openConfirmDialog } from './utilities/useDialog'
import { i18n } from './localization'
import { getWalletLink } from './utilities/useLinks'
import { isUserLoggedIn } from './utilities/api'
import * as Sentry from '@sentry/browser'

onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const redirectAction = urlParams.get('redirect_action')
    const redirectStatus = urlParams.get('status')
    const referralConversionEvent = urlParams.get('referral_conversion_event')
    const hasReferralCode = urlParams.has('referral')
    const hasError = urlParams.has('error')
    const hasKcActionStatus = urlParams.has('kc_action_status')
    const kcActionStatus = urlParams.get('kc_action_status')

    if (redirectAction === 'legal_invite') {
        if (redirectStatus === 'failure') {
            openAlertDialog(
                i18n.global.t('legal_account_managers.dialogs.failure_title'),
                i18n.global.t('legal_account_managers.dialogs.failure_message'),
                {
                    confirmText: i18n.global.t('legal_account_managers.dialogs.failure_button'),
                }
            )
        } else if (redirectStatus === 'success') {
            openConfirmDialog(
                i18n.global.t('legal_account_managers.dialogs.success_title'),
                i18n.global.t('legal_account_managers.dialogs.success_message'),
                {
                    type: 'Success',
                    hideCancel: true,
                    confirmText: i18n.global.t('legal_account_managers.dialogs.success_button'),
                }
            )
        } else if (redirectStatus === 'registration_required') {
            openConfirmDialog(
                i18n.global.t('legal_account_managers.dialogs.registration_required_title'),
                i18n.global.t('legal_account_managers.dialogs.registration_required_message'),
                {
                    type: 'Warning',
                    hideCancel: true,
                    confirmText: i18n.global.t('legal_account_managers.dialogs.registration_required_button'),
                }
            )
        } else if (redirectStatus === 'already_accepted') {
            openConfirmDialog(
                i18n.global.t('legal_account_managers.dialogs.already_accepted_title'),
                i18n.global.t('legal_account_managers.dialogs.already_accepted_message'),
                {
                    type: 'Warning',
                    hideCancel: true,
                    confirmText: i18n.global.t('legal_account_managers.dialogs.already_accepted_button'),
                }
            )
        } else if (redirectStatus === 'legal_account_already_exists') {
            openAlertDialog(
                i18n.global.t('legal_account_managers.dialogs.legal_account_already_exists_title'),
                i18n.global.t('legal_account_managers.dialogs.legal_account_already_exists_message'),
                {
                    confirmText: i18n.global.t('legal_account_managers.dialogs.legal_account_already_exists_button'),
                }
            )
        }
    } else if (redirectAction === 'money-withdrawal-verification') {
        if (redirectStatus === 'expired_link') {
            openAlertDialog(
                i18n.global.t('wallet.dialogs.money_withdrawal_verification_expired_link_title'),
                i18n.global.t('wallet.dialogs.money_withdrawal_verification_expired_link_message'),
                {
                    confirmText: i18n.global.t('wallet.dialogs.failure_button'),
                }
            ).then(() => (window.location.href = getWalletLink()))
        } else if (redirectStatus === 'no_longer_pending') {
            openConfirmDialog(
                i18n.global.t('wallet.dialogs.money_withdrawal_verification_no_longer_pending_title'),
                i18n.global.t('wallet.dialogs.money_withdrawal_verification_no_longer_pending_message'),
                {
                    type: 'Warning',
                    hideCancel: true,
                    confirmText: i18n.global.t('wallet.dialogs.failure_button'),
                }
            ).then(() => (window.location.href = getWalletLink()))
        } else if (redirectStatus === 'success') {
            openConfirmDialog(
                i18n.global.t('wallet.dialogs.money_withdrawal_verification_success_title'),
                i18n.global.t('wallet.dialogs.money_withdrawal_verification_success_message'),
                {
                    type: 'Success',
                    hideCancel: true,
                    confirmText: i18n.global.t('dialog.button.close'),
                }
            )
        }
    } else if (redirectAction === 'referral') {
        if (redirectStatus === 'success') {
            if (referralConversionEvent === 'custom_event') {
                openConfirmDialog(
                    i18n.global.t('referral_program.referral_success_custom_event_title'),
                    i18n.global.t('referral_program.referral_success_custom_event_message'),
                    {
                        type: 'Success',
                        hideCancel: true,
                        confirmText: i18n.global.t('dialog.button.ok'),
                    }
                )
            } else {
                // redemption event and fallback message
                openConfirmDialog(
                    i18n.global.t('referral_program.referral_success_redemption_event_title'),
                    i18n.global.t('referral_program.referral_success_redemption_event_message'),
                    {
                        type: 'Success',
                        hideCancel: true,
                        confirmText: i18n.global.t('dialog.button.ok'),
                    }
                )
            }
        } else if (redirectStatus === 'campaign_not_found') {
            openConfirmDialog(
                i18n.global.t('referral_program.referral_campaign_not_found_title'),
                i18n.global.t('referral_program.referral_campaign_not_found_message'),
                {
                    type: 'Warning',
                    hideCancel: true,
                    confirmText: i18n.global.t('dialog.button.ok'),
                }
            )
        }
    } else if (redirectAction === 'nyala-passphrase-verification') {
        if (redirectStatus === 'success') {
            openConfirmDialog(
                i18n.global.t('nyala_vault.nyala_passphrase_verification_success_title'),
                i18n.global.t('nyala_vault.nyala_passphrase_verification_success_message'),
                {
                    type: 'Success',
                    hideCancel: true,
                    confirmText: i18n.global.t('dialog.button.ok'),
                }
            )
        } else if (redirectStatus === 'expired_link') {
            openConfirmDialog(
                i18n.global.t('nyala_vault.nyala_passphrase_verification_expired_link_title'),
                i18n.global.t('nyala_vault.nyala_passphrase_verification_expired_link_message'),
                {
                    type: 'Warning',
                    hideCancel: true,
                    confirmText: i18n.global.t('dialog.button.ok'),
                }
            )
        }
    } else if (hasReferralCode && !isUserLoggedIn()) {
        openConfirmDialog(
            i18n.global.t('referral_program.referral_info_title'),
            i18n.global.t('referral_program.referral_info_message'),
            {
                type: 'Success',
                hideCancel: true,
                confirmText: i18n.global.t('dialog.button.ok'),
            }
        )
    } else if (hasReferralCode && isUserLoggedIn()) {
        openConfirmDialog(
            i18n.global.t('referral_program.referral_existing_user_title'),
            i18n.global.t('referral_program.referral_existing_user_message'),
            {
                hideCancel: true,
                confirmText: i18n.global.t('dialog.button.ok'),
            }
        )
    } else if (hasError) {
        Sentry.captureException(new Error(`Keycloak Error: ${window.location.href}`))

        openAlertDialog(
            i18n.global.t('security.dialogs.error_title'),
            i18n.global.t('security.dialogs.error_message'),
            {
                confirmText: i18n.global.t('dialog.button.close'),
            }
        )
    } else if (hasKcActionStatus && kcActionStatus === 'success') {
        openConfirmDialog(
            i18n.global.t('security.dialogs.success_title'),
            i18n.global.t('security.dialogs.success_message'),
            {
                type: 'Success',
                hideCancel: true,
                confirmText: i18n.global.t('dialog.button.ok'),
            }
        )

        window.history.pushState({}, document.title, window.location.pathname)
    }
})
</script>

<style scoped></style>
